import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BarChart3, Check, Code2, FileText, Gauge, Globe, Headphones, Layers, LockKeyhole, MessageCircle, Monitor, MonitorCheck, Rocket, Settings, ShieldCheck, Smartphone, TrendingUp } from 'lucide-react';
import SEO from './seo/SEO';

const serviceDetails = {
  'web-solutions': {
    title: 'Web Solutions',
    badge: 'Web & SaaS',
    image: '/websolutions-1.png',
    imageAlt: 'Web solutions team building a digital platform',
    icon: Globe,
    intro: 'Fast, secure web experiences that turn your business goals into useful digital products.',
    description: 'From a first company website to a multi-role SaaS platform, we design and build responsive web solutions around the way your customers and teams work.',
    features: ['Custom web portals and SaaS platforms', 'High-converting landing pages', 'Responsive business websites', 'E-commerce platforms and API integrations'],
    technologies: ['React', 'Next.js', 'Tailwind CSS', 'Node.js'],
    outcomes: ['A smooth experience on every screen', 'Secure, maintainable architecture', 'Connected tools and business workflows'],
  },
  'mobile-apps': {
    title: 'Mobile Apps',
    badge: 'iOS & Android',
    image: '/service-2.png',
    imageAlt: 'Mobile app team reviewing a product together',
    icon: Smartphone,
    intro: 'Mobile products that make everyday tasks easier for customers, employees, and field teams.',
    description: 'We create native and cross-platform applications with clear, intuitive interfaces. Apps can support offline work, notifications, and the systems your business already uses.',
    features: ['Native and cross-platform iOS and Android apps', 'Intuitive interface and product design', 'App Store and Google Play launch support', 'Push notifications and offline capabilities'],
    technologies: ['Flutter', 'React Native', 'Swift', 'Kotlin'],
    outcomes: ['Simple, consistent user journeys', 'Reliable use on the move', 'A clear path from prototype to launch'],
  },
  'desktop-software': {
    title: 'Desktop Software',
    badge: 'ERP & Billing',
    image: '/service-3.png',
    imageAlt: 'Desktop software and business operations dashboard',
    icon: Code2,
    intro: 'Purpose-built business software that brings operations, information, and teams together.',
    description: 'We build custom operating systems for your business, including ERP and CRM platforms, point-of-sale tools, and branch workflows tailored to your processes.',
    features: ['Custom ERP and CRM systems', 'Integrated POS and billing', 'Automated data sync across branches', 'Role-based security and permissions'],
    technologies: ['Python', 'Electron', 'PostgreSQL', 'API integrations'],
    outcomes: ['Less repetitive manual work', 'A shared view of business operations', 'Access and reporting tailored to each role'],
  },
  'digital-marketing': {
    title: 'Digital Marketing',
    badge: 'Growth & Leads',
    image: '/service-4.png',
    imageAlt: 'Marketing team planning a digital growth campaign',
    icon: TrendingUp,
    intro: 'Practical digital campaigns that help the right customers discover and choose your business.',
    description: 'We combine paid media, search visibility, and performance measurement to build campaigns around your audience, goals, and budget.',
    features: ['Google and Meta advertising campaigns', 'Local SEO and technical SEO', 'Qualified B2B lead generation', 'Brand growth and campaign reporting'],
    technologies: ['Google Ads', 'Meta Ads', 'GA4', 'Technical SEO'],
    outcomes: ['Clear campaign goals and measurement', 'More relevant traffic and enquiries', 'Ongoing improvements guided by results'],
  },
};

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = serviceDetails[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <section className="mx-auto max-w-4xl px-5 py-32 text-center">
        <h1 className="text-3xl font-black text-slate-950">Service not found</h1>
        <Link to="/#services" className="mt-6 inline-flex items-center gap-2 font-bold text-blue-700">Back to services <ArrowRight className="h-4 w-4" /></Link>
      </section>
    );
  }

  if (slug === 'web-solutions') return <WebSolutionsLanding />;
  if (slug === 'mobile-apps') return <MobileAppsLanding />;
  if (slug === 'desktop-software') return <DesktopSoftwareLanding />;

  const Icon = service.icon;

  return (
    <div className="bg-white text-slate-900">
      <SEO title={`${service.title} | GlobalEPRC`} description={service.intro} path={`/services/${slug}`} />
      <section className="relative isolate min-h-[480px] overflow-hidden bg-slate-950 pt-28 text-white sm:min-h-[540px] sm:pt-32">
        <img src={service.image} alt={service.imageAlt} className="absolute inset-0 -z-20 h-full w-full object-cover" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-slate-950/95 via-slate-950/75 to-slate-950/25" />
        <div className="mx-auto flex min-h-[380px] max-w-7xl items-center px-5 py-16 sm:min-h-[410px] sm:px-8 lg:px-10">
          <div className="max-w-3xl">
            <Link to="/#services" className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-white/80 transition hover:text-white"><ArrowLeft className="h-4 w-4" /> All services</Link>
            <div className="flex items-center gap-3 text-blue-200">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/20 bg-white/10"><Icon className="h-5 w-5" /></span>
              <span className="text-xs font-black uppercase tracking-[0.2em]">{service.badge}</span>
            </div>
            <h1 className="mt-5 text-4xl font-black tracking-tight sm:text-6xl">{service.title}</h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/85 sm:text-xl">{service.intro}</p>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-3 rounded-xl bg-amber-400 px-6 py-3.5 text-sm font-black text-slate-950 transition hover:bg-amber-300">Talk to our team <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 sm:py-20 lg:grid-cols-[1.1fr_0.9fr] lg:px-10">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.2em] text-blue-700">What we do</p>
          <h2 className="mt-3 text-3xl font-black leading-tight text-slate-950 sm:text-4xl">Technology shaped around your business.</h2>
          <p className="mt-5 max-w-2xl leading-relaxed text-slate-600">{service.description}</p>

          <h3 className="mt-10 text-xl font-extrabold text-slate-950">Our capabilities</h3>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {service.features.map((feature) => (
              <div key={feature} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <aside className="rounded-2xl bg-slate-950 p-6 text-white sm:p-8">
          <h3 className="text-xl font-extrabold">Built for real-world results</h3>
          <ul className="mt-6 space-y-4">
            {service.outcomes.map((outcome) => (
              <li key={outcome} className="flex items-start gap-3 text-sm leading-relaxed text-slate-300">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                <span>{outcome}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 border-t border-white/15 pt-6">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-300">Technologies & channels</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {service.technologies.map((technology) => <span key={technology} className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/85">{technology}</span>)}
            </div>
          </div>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-amber-300 transition hover:text-amber-200">Discuss your project <ArrowRight className="h-4 w-4" /></Link>
        </aside>
      </section>

      <section className="bg-blue-50 px-5 py-12 sm:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-black text-slate-950">Have a project in mind?</h2>
            <p className="mt-2 text-sm text-slate-600">Tell us what you’re building and we’ll help plan the next step.</p>
          </div>
          <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-5 py-3 text-sm font-bold text-white transition hover:bg-blue-700">Contact GlobalEPRC <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </div>
  );
};

const WebSolutionsLanding = () => {
  const buildItems = [
    { title: 'Business Websites', image: '/websolutions-3.png', desc: 'Professional websites that represent your brand and build credibility.' },
    { title: 'E-Commerce Solutions', image: '/websolutions-4.png', desc: 'Online stores with secure payment integration and a smooth shopping experience.' },
    { title: 'Web Applications', image: '/websolutions-5.png', desc: 'Custom web applications tailored to your unique business requirements.' },
    { title: 'CMS Development', image: '/websolutions-6.png', desc: 'Easy-to-manage websites using platforms like WordPress and custom CMS solutions.' },
  ];
  const processSteps = [
    ['01', 'Requirement Analysis', 'Understanding your goals and audience.'],
    ['02', 'Planning & Strategy', 'Creating the right approach and roadmap.'],
    ['03', 'Design & Development', 'Building a modern, scalable solution.'],
    ['04', 'Testing & Optimization', 'Ensuring quality, performance and security.'],
    ['05', 'Launch & Support', 'Going live with continuous support.'],
  ];

  return (
    <div id="top" className="bg-[#f8f6f1] font-sans text-slate-900">
      <SEO title="Web Solutions | GlobalEPRC" description="Custom web solutions tailored to your business, from responsive websites and e-commerce platforms to web applications and CMS development." path="/services/web-solutions" />

      <main>
        <section className="relative isolate flex min-h-[580px] items-center overflow-hidden bg-slate-950 pt-16 text-white sm:min-h-[640px] sm:pt-20">
          <img src="/websolutions-1.png" alt="Web design team planning a digital experience" className="absolute inset-0 -z-20 h-full w-full object-cover object-center" />
          <div className="absolute inset-0 -z-10 bg-gradient-to-r from-black/85 via-black/55 to-black/10" />
          <div className="mx-auto w-full max-w-7xl px-5 py-14 sm:px-8 sm:py-16 lg:px-10">
            <div className="max-w-[560px]">
              <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#e6b96f]">Web Solutions</p>
              <h1 className="mt-3 font-serif text-4xl leading-[1.05] sm:text-5xl">Web Experiences<br />Built for <span className="text-[#e6b96f]">Real Growth</span></h1>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-white/90">From simple business websites to complex web applications, we create digital experiences that help you attract, engage and grow.</p>
              <Link to="/contact" className="mt-5 inline-flex items-center gap-2 rounded-md bg-[#e5b76b] px-5 py-3 text-[11px] font-bold text-slate-950 transition hover:bg-[#f1ca87]">Discuss Your Project <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20 lg:px-10">
          <div className="grid items-center gap-7 lg:grid-cols-[1.08fr_0.92fr] lg:gap-10">
            <img src="/websolutions-2.png" alt="Web designers reviewing website layouts together" className="h-[350px] w-full rounded-md object-cover shadow-sm sm:h-[420px]" />
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#bd8741]">Overview</p>
              <h2 className="mt-2 font-serif text-3xl leading-tight text-slate-950 sm:text-[34px]">Custom Web Solutions<br />Tailored to <span className="text-[#bd8741]">Your Business</span></h2>
              <p className="mt-3 text-[13px] leading-relaxed text-slate-600">We design and develop websites that are user-friendly, visually engaging and built to perform. Whether you need a corporate website, an e-commerce platform or a complex web application, our team delivers solutions that fit your goals and industry.</p>
              <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-4 text-[11px] font-bold text-slate-800">
                <div className="flex items-center gap-3"><Monitor className="h-7 w-7 shrink-0 text-[#bd8741]" /><span>Modern &amp; Engaging<br />Designs</span></div>
                <div className="flex items-center gap-3"><Smartphone className="h-7 w-7 shrink-0 text-[#bd8741]" /><span>Responsive<br />Across All Devices</span></div>
                <div className="flex items-center gap-3"><ShieldCheck className="h-7 w-7 shrink-0 text-[#bd8741]" /><span>Secure &amp; Scalable<br />Architecture</span></div>
                <div className="flex items-center gap-3"><BarChart3 className="h-7 w-7 shrink-0 text-[#bd8741]" /><span>SEO-Friendly<br />Structure</span></div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 pb-14 pt-8 sm:px-8 sm:pb-16 sm:pt-10 lg:px-10">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#bd8741]">Services</p>
              <h2 className="mt-1 font-serif text-3xl text-slate-950 sm:text-4xl">What We Build</h2>
            </div>
            <a href="#process" className="mb-1 hidden items-center gap-2 text-[11px] font-medium text-slate-700 underline decoration-[#bd8741]/60 underline-offset-4 sm:inline-flex">Explore All Services <ArrowRight className="h-3.5 w-3.5 text-[#bd8741]" /></a>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {buildItems.map((item) => (
              <article key={item.title} className="group overflow-hidden rounded-md border border-[#e8e0d4] bg-white transition hover:-translate-y-1 hover:shadow-lg">
                <div className="h-36 overflow-hidden sm:h-40"><img src={item.image} alt={item.title} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" /></div>
                <div className="p-5">
                  <h3 className="text-sm font-extrabold text-slate-900">{item.title}</h3>
                  <p className="mt-3 min-h-[60px] text-xs leading-relaxed text-slate-600">{item.desc}</p>
                  <a href="#process" aria-label={`Learn about ${item.title}`} className="mt-2 inline-flex h-6 w-6 items-center justify-center rounded-full border border-[#eee4d5] text-[#bd8741] transition group-hover:bg-[#e5b76b] group-hover:text-slate-950"><ArrowRight className="h-3.5 w-3.5" /></a>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section id="process" className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-16 lg:px-10">
          <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#bd8741]">Our Process</p>
          <h2 className="mt-2 font-serif text-4xl text-slate-950 sm:text-5xl">From Idea to Launch</h2>
          <div className="relative mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-5 lg:gap-5">
            <div className="absolute left-5 right-5 top-5 hidden border-t border-[#e0cfb4] lg:block" />
            {processSteps.map(([number, title, description]) => (
              <div key={number} className="relative">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#101b25] text-xs font-bold text-white shadow-sm">{number}</span>
                <h3 className="mt-3 text-sm font-extrabold text-slate-900">{title}</h3>
                <p className="mt-2 max-w-[190px] text-sm leading-relaxed text-slate-600">{description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-5 bg-[#07111b] text-white">
          <div className="grid min-h-[270px] lg:grid-cols-2">
            <div className="flex items-center px-5 py-10 sm:px-8 lg:justify-start lg:py-12 lg:pl-10 lg:pr-12">
              <div className="max-w-lg">
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#e6b96f]">Why Choose GlobalEPRC</p>
                <h2 className="mt-3 font-serif text-3xl leading-tight sm:text-4xl">More Than Just<br />a <span className="text-[#e6b96f]">Website</span></h2>
                <p className="mt-3 max-w-md text-base leading-relaxed text-white/80 sm:text-lg">We focus on creating web solutions that not only look great but also deliver real business value. Our team combines technical expertise with a deep understanding of user experience and industry needs.</p>
                <Link to="/contact" className="mt-5 inline-flex items-center gap-2 rounded-md bg-[#e5b76b] px-5 py-3 text-[11px] font-bold text-slate-950 transition hover:bg-[#f1ca87]">Get a Quote <ArrowRight className="h-3.5 w-3.5" /></Link>
              </div>
            </div>
            <div className="min-h-[220px] overflow-hidden lg:min-h-full"><img src="/websolutions-7.png" alt="GlobalEPRC team working in a modern office" className="h-full min-h-[220px] w-full object-cover" /></div>
          </div>
        </section>

        <section id="contact" className="bg-cover bg-center px-5 py-10 text-center sm:py-12" style={{ backgroundImage: "url('/websolutions-8.png')" }}>
          <p className="text-[9px] font-black uppercase tracking-[0.25em] text-slate-700">Let’s Build Together</p>
          <h2 className="mt-2 font-serif text-3xl text-slate-950 sm:text-4xl">Ready to Start Your Web Project?</h2>
          <div className="mx-auto mt-3 flex max-w-4xl flex-col items-center gap-4 text-center">
            <p className="flex-1 text-center text-sm leading-relaxed text-slate-700 sm:text-base">Partner with GlobalEPRC and get a custom web solution that helps your business stand out and grow.</p>
            <Link to="/contact" className="inline-flex shrink-0 items-center gap-2 rounded-md bg-[#e5b76b] px-5 py-3 text-[11px] font-bold text-slate-950 transition hover:bg-[#f1ca87]">Contact Us <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
        </section>
      </main>
    </div>
  );
};

const MobileAppsLanding = () => {
  const mobileServices = [
    { title: 'Android App Development', description: 'Custom Android apps for a wide range of devices.', icon: Smartphone },
    { title: 'iOS App Development', description: 'High-quality iOS apps built for the Apple ecosystem.', icon: Smartphone },
    { title: 'Cross-Platform Development', description: 'Cost-effective solutions using Flutter and React Native.', icon: Layers },
    { title: 'Maintenance & Support', description: 'Ongoing updates, feature enhancements and technical support.', icon: Headphones },
  ];
  const appShowcase = [
    { title: 'Food Ordering App', image: '/mobileapp-3.png', description: 'A convenient food ordering experience with real-time tracking.' },
    { title: 'Learning App', image: '/mobileapp-4.png', description: 'A feature-rich learning platform with interactive content.' },
    { title: 'Travel App', image: '/mobileapp-5.png', description: 'A travel companion app with curated recommendations and bookings.' },
  ];
  const benefits = [
    'User-focused and intuitive design',
    'Scalable and secure architecture',
    'Integration with third-party services',
    'Quality testing for smooth performance',
    'Post-launch support and maintenance',
  ];

  return (
    <div className="bg-[#faf8f4] font-sans text-slate-900">
      <SEO title="Mobile App Development | GlobalEPRC" description="User-friendly Android and iOS applications built around your business goals." path="/services/mobile-apps" />
      <main>
        <section className="relative isolate flex min-h-[520px] items-center overflow-hidden bg-[#f7f3eb] pt-20 sm:min-h-[600px] sm:pt-24">
          <img src="/mobileapp-1.png" alt="Food ordering mobile app displayed on a phone" className="absolute inset-0 -z-20 h-full w-full object-cover object-center" />
          <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#faf8f4]/45 via-[#faf8f4]/20 to-transparent" />
          <div className="mx-auto w-full max-w-7xl px-5 py-12 sm:px-8 lg:px-10">
            <div className="max-w-[520px]">
              <p className="inline-flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-800">Mobile Apps <span className="h-px w-12 bg-[#c99148]" /></p>
              <h1 className="mt-6 font-serif text-4xl leading-[1.08] text-slate-950 sm:text-5xl">Mobile Applications<br />for Growing Businesses</h1>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-700 sm:text-base">We design and develop Android and iOS applications that are user-friendly, scalable and built around your business goals.</p>
              <Link to="/contact" className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#d9aa62] px-5 py-3 text-xs font-bold text-slate-950 transition hover:bg-[#e9bd79]">Discuss Your Project <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-10 sm:px-8 sm:py-14 lg:px-10">
          <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <h2 className="flex items-center gap-4 font-serif text-3xl text-slate-950">Our Services <span className="h-px w-12 bg-[#c99148]" /></h2>
            <p className="max-w-xs text-xs leading-relaxed text-slate-600">End-to-end mobile app development services to help you turn your ideas into reliable and high-performing applications.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-0">
            {mobileServices.map(({ title, description, icon: Icon }, index) => (
              <article key={title} className={`px-1 py-2 sm:px-5 lg:px-7 ${index < mobileServices.length - 1 ? 'lg:border-r lg:border-[#e9e1d5]' : ''}`}>
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#f4ecdf] text-[#b27a30]"><Icon className="h-7 w-7" /></span>
                <h3 className="mt-5 max-w-[190px] font-serif text-lg font-bold leading-tight text-slate-950">{title}</h3>
                <p className="mt-3 max-w-[220px] text-xs leading-relaxed text-slate-600">{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="grid items-center gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-12">
          <div className="h-[260px] overflow-hidden sm:h-[340px] lg:h-[380px]">
            <img src="/mobileapp-2.png" alt="Mobile app developer working on application code" className="h-full w-full object-cover object-center" />
          </div>
          <div className="px-5 pb-8 sm:px-8 lg:px-10 lg:py-10">
            <h2 className="flex items-center gap-4 font-serif text-3xl text-slate-950">Why Choose Us <span className="h-px w-12 bg-[#c99148]" /></h2>
            <ul className="mt-7 space-y-4">
              {benefits.map((benefit) => (
                <li key={benefit} className="flex items-center gap-4 text-sm text-slate-700"><span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#c99148]" />{benefit}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-10 sm:px-8 sm:py-14 lg:px-10">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <h2 className="flex items-center gap-4 font-serif text-3xl text-slate-950">Our Work <span className="h-px w-12 bg-[#c99148]" /></h2>
            <p className="max-w-xs text-xs leading-relaxed text-slate-600">A few of the mobile applications we have developed for different industries.</p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {appShowcase.map((app) => (
              <article key={app.title}>
                <div className="h-48 overflow-hidden rounded-md sm:h-52"><img src={app.image} alt={app.title} className="h-full w-full object-cover transition duration-500 hover:scale-105" /></div>
                <h3 className="mt-3 font-serif text-base font-bold text-slate-950">{app.title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-600">{app.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="bg-cover bg-center px-5 py-12 sm:py-16" style={{ backgroundImage: "url('/mobileapp-6.png')" }}>
          <div className="mx-auto flex max-w-7xl flex-col items-center gap-6 sm:flex-row sm:justify-center sm:gap-12">
            <div className="max-w-lg">
              <p className="text-[9px] font-black uppercase tracking-[0.25em] text-slate-700">Let’s Build Together</p>
              <h2 className="mt-2 font-serif text-3xl text-slate-950 sm:text-4xl">Have a Mobile App Idea?</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-700">Partner with GlobalEPRC and get a mobile application that helps your business grow.</p>
            </div>
            <Link to="/contact" className="inline-flex shrink-0 items-center gap-2 rounded-md bg-[#d9aa62] px-6 py-3 text-xs font-bold text-slate-950 transition hover:bg-[#e9bd79]">Get in Touch <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
        </section>
      </main>
    </div>
  );
};

const DesktopSoftwareLanding = () => {
  const advantages = [
    { title: 'Custom Solutions', description: 'Tailored to your unique business requirements.', icon: Settings },
    { title: 'High Performance', description: 'Fast, reliable and efficient even for complex tasks.', icon: Gauge },
    { title: 'Data Security', description: 'Complete control over your data and system.', icon: LockKeyhole },
    { title: 'Offline Access', description: 'Works without internet for uninterrupted operations.', icon: MonitorCheck },
  ];
  const capabilities = [
    'Inventory & Billing Management',
    'HR & Payroll Systems',
    'Accounting & Finance Tools',
    'Customer Relationship Management',
    'Custom Business Applications',
  ];
  const process = [
    { title: '1. Consultation', description: 'Understand your requirements and goals.', icon: MessageCircle },
    { title: '2. Planning', description: 'Define features and design the solution.', icon: FileText },
    { title: '3. Development', description: 'Build and test the application.', icon: Code2 },
    { title: '4. Deployment', description: 'Launch and provide ongoing support.', icon: Rocket },
  ];

  return (
    <div className="bg-[#fbf8f2] font-sans text-slate-900">
      <SEO title="Desktop Software | GlobalEPRC" description="Custom desktop software for inventory, billing, HR, finance and business operations." path="/services/desktop-software" />
      <main>
        <section className="relative isolate flex min-h-[560px] items-center overflow-hidden bg-[#f7f2e9] pt-20 sm:min-h-[660px] sm:pt-24">
          <img src="/desktop-software-1.png" alt="Business desktop software dashboard on a computer monitor" className="absolute inset-0 -z-20 h-full w-full object-cover object-[58%_center]" />
          <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#fbf8f2]/95 via-[#fbf8f2]/75 to-transparent" />
          <div className="mx-auto w-full max-w-7xl px-5 py-16 sm:px-8 lg:px-10">
            <div className="max-w-[470px]">
              <p className="inline-flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-800">Desktop Software <span className="h-px w-12 bg-[#c99148]" /></p>
              <h1 className="mt-6 font-serif text-4xl leading-[1.08] text-slate-950 sm:text-5xl">Reliable Desktop<br />Software Solutions</h1>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-700 sm:text-base">We design and develop custom desktop applications that simplify your business operations, improve productivity and deliver secure, high-performance solutions tailored to your needs.</p>
              <Link to="/contact" className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#d9aa62] px-5 py-3 text-xs font-bold text-slate-950 transition hover:bg-[#e9bd79]">Discuss Your Project <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-12 text-center sm:px-8 sm:py-16 lg:px-10">
          <p className="inline-flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-700">Why Choose Desktop Software <span className="h-px w-12 bg-[#c99148]" /></p>
          <h2 className="mt-3 font-serif text-3xl text-slate-950 sm:text-4xl">Built for Better Business Operations</h2>
          <div className="mt-9 grid gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {advantages.map(({ title, description, icon: Icon }) => (
              <article key={title} className="flex flex-col items-center px-3">
                <span className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-[#f5e9d9] text-slate-900"><Icon className="h-7 w-7" /></span>
                <h3 className="mt-3 text-sm font-bold">{title}</h3>
                <p className="mt-1 max-w-[210px] text-sm leading-relaxed text-slate-600">{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="grid items-center gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-12">
          <div className="h-[280px] overflow-hidden sm:h-[390px] lg:h-[430px]">
            <img src="/desktop-software-2.png" alt="Desktop business application dashboard for inventory and billing" className="h-full w-full object-cover object-center" />
          </div>
          <div className="px-5 pb-8 sm:px-8 lg:px-10 lg:py-10">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#bd8741]">Our Solutions</p>
            <h2 className="mt-3 max-w-lg font-serif text-3xl leading-tight text-slate-950 sm:text-4xl">Desktop Applications<br />for Your Business</h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-slate-600 sm:text-base">From inventory and billing systems to HR and finance tools, we develop desktop software that helps you manage your business efficiently.</p>
            <ul className="mt-5 space-y-2.5">
              {capabilities.map((capability) => (
                <li key={capability} className="flex items-center gap-3 text-sm text-slate-700"><span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#d9aa62] text-slate-950"><Check className="h-3 w-3" /></span>{capability}</li>
              ))}
            </ul>
            <Link to="/contact" className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#d9aa62] px-5 py-3 text-xs font-bold text-slate-950 transition hover:bg-[#e9bd79]">Learn More <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-14 text-center sm:px-8 sm:py-16 lg:px-10">
          <p className="inline-flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-700">Our Development Process <span className="h-px w-12 bg-[#c99148]" /></p>
          <h2 className="mt-3 font-serif text-3xl text-slate-950 sm:text-4xl">From Idea to Application</h2>
          <div className="relative mt-9 grid gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            <div className="absolute left-[12%] right-[12%] top-9 hidden border-t border-dotted border-[#c9b79e] lg:block" />
            {process.map(({ title, description, icon: Icon }) => (
              <article key={title} className="relative flex flex-col items-center">
                <span className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-[#f5e9d9] text-slate-900"><Icon className="h-7 w-7" /></span>
                <h3 className="mt-3 text-sm font-bold">{title}</h3>
                <p className="mt-1 max-w-[210px] text-sm leading-relaxed text-slate-600">{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 pb-8 sm:px-8 sm:pb-12 lg:px-10">
          <div className="relative isolate overflow-hidden rounded-lg bg-[#f1e4d3] bg-cover bg-center px-6 py-10 sm:px-10 sm:py-14" style={{ backgroundImage: "url('/desktop-software-3.png')" }}>
            <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#f5e9d9] via-[#f5e9d9]/95 to-[#f5e9d9]/10" />
            <div className="max-w-lg">
              <p className="inline-flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-700">Let’s Build Together <span className="h-px w-12 bg-[#c99148]" /></p>
              <h2 className="mt-3 font-serif text-3xl text-slate-950 sm:text-4xl">Need a Custom Desktop Solution?</h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-700 sm:text-base">Get in touch with us and let’s discuss how we can build a solution for your business.</p>
              <Link to="/contact" className="mt-5 inline-flex items-center gap-2 rounded-md bg-[#d9aa62] px-5 py-3 text-xs font-bold text-slate-950 transition hover:bg-[#e9bd79]">Get in Touch <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ServiceDetailPage;
